import { logger, sanitizeError } from '@heidi/runtime/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_TEAM_DETAILS } from '../../contracts/keys';
import { toggleTeamPatientRetention } from '../api/teams';

type ToggleTeamPatientRetentionVariables = {
  teamId: string;
  patientRetentionEnabled: boolean;
  retentionDays?: number;
};

export interface UseMutateToggleTeamPatientRetentionOptions {
  /** App-reaction port: fired where `invalidateCurrentUser()` fired. */
  onCurrentUserRefreshNeeded?: () => void;
  /** App-reaction port: fired where the legacy success toast fired. */
  onRetentionUpdated?: () => void;
  /** App-reaction port: fired where the legacy destructive toast fired. */
  onRetentionUpdateError?: (error: Error) => void;
}

export function useMutateToggleTeamPatientRetention(
  options?: UseMutateToggleTeamPatientRetentionOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['toggleTeamPatientRetention'],
    mutationFn: async (input: ToggleTeamPatientRetentionVariables) => {
      await toggleTeamPatientRetention({
        teamId: input.teamId,
        patientRetentionEnabled: input.patientRetentionEnabled,
        retentionDays: input.retentionDays,
      });
    },
    onSuccess: async (_, input) => {
      await queryClient.invalidateQueries({
        queryKey: [GET_TEAM_DETAILS, { teamId: input.teamId }],
      });

      options?.onCurrentUserRefreshNeeded?.();
      options?.onRetentionUpdated?.();
    },
    onError: (error) => {
      logger.error(
        'Error updating team patient retention settings:',
        sanitizeError(error)
      );
      options?.onRetentionUpdateError?.(error);
    },
  });
}
